/**
 * Battle View Dashboard — OR Materials Manager's discrepancy view.
 * Left: Standard Preference Card items. Right: Live dictation-extracted items.
 * Center: Discrepancy summary with waste/ad-hoc indicators.
 */

import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  AlertTriangle,
  CheckCircle2,
  Package,
  Mic,
  ArrowLeftRight,
  RefreshCw,
  ShieldAlert,
  TrendingDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SupplyItem, ORSyncResult, DiscrepancyType } from '@/lib/supply/orSyncEngine';
import { compareCardToDictation } from '@/lib/supply/orSyncEngine';
import { DEMO_BUNDLES, BUNDLE_OPTIONS } from '@/lib/supply/demoData';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const CATEGORY_LABELS: Record<SupplyItem['category'], string> = {
  implant: 'Implant',
  suture: 'Suture',
  instrument: 'Instrument',
  consumable: 'Consumable',
  graft: 'Graft',
  cement: 'Cement',
};

function categoryColor(cat: SupplyItem['category']): string {
  const map: Record<string, string> = {
    implant: 'bg-primary/20 text-primary border-primary/30',
    suture: 'bg-chart-2/20 text-chart-2 border-chart-2/30',
    instrument: 'bg-chart-3/20 text-chart-3 border-chart-3/30',
    consumable: 'bg-muted text-muted-foreground border-border',
    graft: 'bg-success/20 text-success border-success/30',
    cement: 'bg-warning/20 text-warning border-warning/30',
  };
  return map[cat] || 'bg-muted text-muted-foreground';
}

function SupplyItemRow({ item, highlight }: { item: SupplyItem; highlight?: 'waste' | 'adhoc' | 'matched' }) {
  return (
    <div
      className={cn(
        'flex items-center justify-between px-3 py-2 rounded-lg border transition-colors',
        highlight === 'waste' && 'bg-destructive/10 border-destructive/30',
        highlight === 'adhoc' && 'bg-warning/10 border-warning/30',
        highlight === 'matched' && 'bg-success/5 border-success/20',
        !highlight && 'border-border bg-card',
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        {highlight === 'waste' && <ShieldAlert className="w-4 h-4 text-destructive shrink-0" />}
        {highlight === 'adhoc' && <AlertTriangle className="w-4 h-4 text-warning shrink-0" />}
        {highlight === 'matched' && <CheckCircle2 className="w-4 h-4 text-success shrink-0" />}
        <span className="text-sm font-medium truncate">{item.name}</span>
        {item.size && (
          <span className="text-xs text-muted-foreground">({item.size})</span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge variant="outline" className={cn('text-[10px] px-1.5', categoryColor(item.category))}>
          {CATEGORY_LABELS[item.category]}
        </Badge>
        <span className="text-xs text-muted-foreground">×{item.quantity}</span>
      </div>
    </div>
  );
}

export function BattleViewDashboard() {
  const [synced, setSynced] = useState(true);
  const [selectedCpt, setSelectedCpt] = useState('27130');

  const bundle = DEMO_BUNDLES[selectedCpt];

  const result: ORSyncResult = useMemo(
    () => compareCardToDictation(bundle.card.items, bundle.extractedItems),
    [selectedCpt, bundle],
  );

  // Build lookup for highlight type per item
  const cardHighlights = useMemo(() => {
    const map = new Map<string, DiscrepancyType>();
    for (const d of result.discrepancies) {
      if (d.cardItem) map.set(d.cardItem.id, d.type);
    }
    return map;
  }, [result]);

  const dictHighlights = useMemo(() => {
    const map = new Map<string, DiscrepancyType>();
    for (const d of result.discrepancies) {
      if (d.dictationItem) map.set(d.dictationItem.id, d.type);
    }
    return map;
  }, [result]);

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border">
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="w-4 h-4 text-success" />
              <span className="text-xs text-muted-foreground">Matched</span>
            </div>
            <p className="text-2xl font-bold text-success">{result.matchedCount}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="w-4 h-4 text-destructive" />
              <span className="text-xs text-muted-foreground">Potential Waste</span>
            </div>
            <p className="text-2xl font-bold text-destructive">{result.wasteCount}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-warning" />
              <span className="text-xs text-muted-foreground">Ad-hoc Pulls</span>
            </div>
            <p className="text-2xl font-bold text-warning">{result.adhocCount}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-2 mb-1">
              <ArrowLeftRight className="w-4 h-4 text-primary" />
              <span className="text-xs text-muted-foreground">Match Rate</span>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-primary">{result.matchRate}%</p>
            </div>
            <Progress value={result.matchRate} className="h-1.5 mt-1" />
          </CardContent>
        </Card>
      </div>

      {/* Procedure Context */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-lg">OR-Sync Battle View</CardTitle>
              <CardDescription>
                CPT {bundle.card.cptCode} — {bundle.card.procedureName} · {bundle.card.surgeonName}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={selectedCpt} onValueChange={setSelectedCpt}>
                <SelectTrigger className="w-[240px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BUNDLE_OPTIONS.map(opt => (
                    <SelectItem key={opt.cpt} value={opt.cpt}>
                      CPT {opt.cpt} — {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSynced(prev => !prev)}
                className="gap-2"
              >
                <RefreshCw className={cn('w-4 h-4', synced && 'text-success')} />
                {synced ? 'Live Synced' : 'Re-sync'}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Battle View — Two Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT: Preference Card */}
        <Card className="border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              <div>
                <CardTitle className="text-base">The Card</CardTitle>
                <CardDescription className="text-xs">
                  Standard Preference Card · {result.cardItems.length} items pulled
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="pt-3 space-y-1.5 max-h-[500px] overflow-y-auto">
            {result.cardItems.map(item => (
              <SupplyItemRow
                key={item.id}
                item={item}
                highlight={cardHighlights.get(item.id)}
              />
            ))}
          </CardContent>
        </Card>

        {/* RIGHT: Live Reality */}
        <Card className="border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-primary" />
              <div>
                <CardTitle className="text-base">The Live Reality</CardTitle>
                <CardDescription className="text-xs">
                  Dictation-Confirmed Items · {result.dictationItems.length} items detected
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="pt-3 space-y-1.5 max-h-[500px] overflow-y-auto">
            {result.dictationItems.map(item => (
              <SupplyItemRow
                key={item.id}
                item={item}
                highlight={dictHighlights.get(item.id)}
              />
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Discrepancy Log */}
      <Card className="border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Discrepancy Log</CardTitle>
          <CardDescription className="text-xs">
            All items requiring Materials Manager attention
          </CardDescription>
        </CardHeader>
        <Separator />
        <CardContent className="pt-3 space-y-2">
          {result.discrepancies
            .filter(d => d.type !== 'matched')
            .map(d => (
              <div
                key={d.id}
                className={cn(
                  'flex items-start gap-3 px-3 py-2.5 rounded-lg border',
                  d.type === 'waste' && 'bg-destructive/5 border-destructive/20',
                  d.type === 'adhoc' && 'bg-warning/5 border-warning/20',
                )}
              >
                {d.type === 'waste' ? (
                  <ShieldAlert className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-warning mt-0.5 shrink-0" />
                )}
                <div>
                  <p className="text-sm font-medium">{d.description}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {d.type === 'waste'
                      ? 'Potential Waste / Restock — item was pulled but not used'
                      : 'Missing from Card / Ad-hoc Pull — update preference card'}
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    'ml-auto shrink-0 text-[10px]',
                    d.type === 'waste' ? 'border-destructive/40 text-destructive' : 'border-warning/40 text-warning',
                  )}
                >
                  {d.type === 'waste' ? 'WASTE' : 'AD-HOC'}
                </Badge>
              </div>
            ))}
          {result.discrepancies.filter(d => d.type !== 'matched').length === 0 && (
            <div className="text-center py-8 text-muted-foreground text-sm">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-success" />
              Perfect match — no discrepancies detected.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
